import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { makeStyles } from 'tss-react/mui';
import {
  Stack,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import ActorCard from 'components/cards/ActorCard';
import useExcelExport from '../../../hooks/useExcelExport';

const useStyles = makeStyles()((theme) => ({
  '@media print': {
    header: { display: 'none !important' },
    stack: { display: 'block !important', '& > *': { breakInside: 'avoid' } },
  },
  actors: {
    width: '100%',
    margin: '0',
    paddingBottom: 66,
    padding: '10px 2em',
    [theme.breakpoints.down('md')]: { padding: '0px 0em' },
  },
  header: { display: 'flex', width: '100%', alignItems: 'center' },
  title: { color: '#2C367E', fontSize: '2.3em', display: 'flex', flex: 1 },
  loading: { display: 'flex', justifyContent: 'center', padding: '20px 0' },
  alphabetNav: {
    position: 'fixed',
    [theme.breakpoints.up('sm')]: { right: '25px', top: '100px' },
    [theme.breakpoints.down('md')]: { right: '0px', top: '90px' },
    alignItems: 'center',
    zIndex: 1000,
    backgroundColor: 'white',
    display: 'flex',
    flexDirection: 'column',
  },
  alphabetItem: {
    cursor: 'pointer',
    fontSize: '1.1em',
    width: '30px',
    height: '30px',
    transition: 'color 0.3s',
    color: 'black',
    '&:hover': { color: 'rgb(217, 101, 82)', borderRadius: '50%' },
  },
  activeLetter: { color: 'rgb(44, 54, 126)' },
  activeCursor: {
    position: 'absolute',
    width: '3px',
    height: '30px',
    color: 'rgb(44, 54, 126)',
    transition: 'top 0.1s',
    borderRadius: '2px',
    zIndex: 999,
  },
  actorContainer: { scrollMarginTop: '100px' },
}));

const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

interface Actor {
  id: string | number;
  name: string;
  url?: string;
  address?: string;
  city?: string;
  shortDescription?: string;
  [key: string]: any;
}

interface ActorsProps {
  data?: { actors: Actor[] };
}

const Actors = ({ data }: ActorsProps) => {
  const { classes } = useStyles();
  const theme = useTheme();
  const exportData = useExcelExport();
  const matches = useMediaQuery(theme.breakpoints.up('sm'));

  const compare = useCallback((a: Actor, b: Actor) => {
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'accent' });
  }, []);

  const [visibleActors, setVisibleActors] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const actorRefs = useRef<Record<string | number, HTMLElement | null>>({});
  const [cursorPosition, setCursorPosition] = useState(-1);
  const [dragging, setDragging] = useState(false);
  const [scrollTargetId, setScrollTargetId] = useState<string | number | null>(null);
  const [isAlphabetVisible, setIsAlphabetVisible] = useState(matches);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const sortedActors = useMemo(() => {
    return data ? data.actors.slice().sort(compare) : [];
  }, [data, compare]);

  const loadMoreActors = useCallback(() => {
    if (isLoading) return;
    setIsLoading(true);
    setTimeout(() => {
      setVisibleActors((prev) => Math.min(prev + 10, sortedActors.length));
      setIsLoading(false);
    }, 10);
  }, [isLoading, sortedActors.length]);

  const observer = useRef<IntersectionObserver | undefined>(undefined);
  const lastActorRef = useCallback(
    (node: HTMLElement | null) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && visibleActors < sortedActors.length) {
          loadMoreActors();
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoading, loadMoreActors, visibleActors, sortedActors.length],
  );

  const handleClickPrint = useCallback(() => window.print(), []);

  const handleClickExport = useCallback(async () => {
    const actorsToExport = sortedActors.map((actor) => ({
      ...actor,
      url: `${process.env.NEXT_PUBLIC_BASE_URL}/acteur/${actor.url}`,
    }));
    await exportData({
      data: actorsToExport,
      columns: ['id', 'name', 'address', 'city', 'shortDescription', 'url'],
      columnLabels: ['ID', 'Nom', 'Adresse', 'Ville', 'Description', 'URL'],
      columnOptions: [{ wch: 4 }, { wch: 30 }, { wch: 30 }, { wch: 30 }, { wch: 60 }, { wch: 50 }],
      sheetName: 'acteurs',
      fileName: 'acteurs',
    });
  }, [sortedActors, exportData]);

  const scrollToLetter = useCallback(
    (letter: string) => {
      const actorsStartingWithLetter = sortedActors.filter((actor) =>
        actor.name.toUpperCase().startsWith(letter),
      );
      if (actorsStartingWithLetter.length > 0) {
        setVisibleActors(
          sortedActors.indexOf(actorsStartingWithLetter[0]) + actorsStartingWithLetter.length,
        );
        setScrollTargetId(actorsStartingWithLetter[0].id);
      } else {
        setVisibleActors(sortedActors.length);
        const updatedFirstActor = sortedActors.find((actor) =>
          actor.name.toUpperCase().startsWith(letter),
        );
        if (updatedFirstActor) setScrollTargetId(updatedFirstActor.id);
      }
    },
    [sortedActors],
  );

  const handleMouseEnter = (index: number) => setCursorPosition(index);
  const handleMouseLeave = () => setCursorPosition(-1);
  const handleMouseDown = (event: React.MouseEvent<HTMLElement>) => {
    setDragging(true);
    handleMouseEnter(parseInt(String((event.currentTarget as any).dataset.index)));
  };
  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (dragging) {
      const index = Math.floor(
        (event.clientY - event.currentTarget.getBoundingClientRect().top) / 30,
      );
      if (index >= 0 && index < alphabet.length) setCursorPosition(index);
    }
  };
  const handleMouseUp = () => setDragging(false);

  const handleScroll = useCallback(() => {
    if (window.scrollY > 200) setIsAlphabetVisible(true);
    else setIsAlphabetVisible(false);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setIsAlphabetVisible(false), 2000);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [handleScroll]);

  useEffect(() => {
    if (scrollTargetId) {
      const actorElement = actorRefs.current[scrollTargetId];
      if (actorElement) actorElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setScrollTargetId(null);
    }
  }, [scrollTargetId]);

  useEffect(() => {
    if (cursorPosition >= 0) {
      const letter = alphabet[cursorPosition];
      scrollToLetter(letter);
    }
  }, [cursorPosition, scrollToLetter]);

  return (
    <div className={classes.actors} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp}>
      <div className={classes.header}>
        <h1 className={classes.title}>Liste des acteurs</h1>
        {matches && (
          <div>
            <Tooltip title="Imprimer">
              <IconButton onClick={handleClickPrint} size="large">
                <PrintIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Exporter">
              <IconButton onClick={handleClickExport} size="large">
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </div>
        )}
      </div>
      <Stack spacing={2} className={classes.stack}>
        {sortedActors.slice(0, visibleActors).map((actor, index) => {
          if (index === visibleActors - 1) {
            return (
              <div ref={lastActorRef} key={actor.id}>
                <ActorCard
                  ref={(el: HTMLElement | null) => { actorRefs.current[actor.id] = el; }}
                  key={actor.id}
                  actor={actor}
                />
              </div>
            );
          }
          return (
            <div key={actor.id} ref={(el) => { actorRefs.current[actor.id] = el; }}>
              <ActorCard key={actor.id} actor={actor} />
            </div>
          );
        })}
      </Stack>
      {isLoading && (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      )}
      {isAlphabetVisible && (
        <Box className={classes.alphabetNav} onMouseLeave={handleMouseLeave}>
          {alphabet.map((letter, index) => (
            <Typography
              key={letter}
              className={`${classes.alphabetItem} ${cursorPosition === index ? classes.activeLetter : ''}`}
              onClick={() => scrollToLetter(letter)}
              data-index={index}
            >
              {letter}
            </Typography>
          ))}
        </Box>
      )}
      {cursorPosition >= 0 && (
        <div
          className={classes.activeCursor}
          style={{ top: `${cursorPosition * 30 + 100}px` }}
        />
      )}
    </div>
  );
};

export default Actors;
